// src/components/warehouse/TransferRequestModal.tsx
import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { WarehouseLocationsService, type Warehouse } from "@/services/warehouse-locations.service";
import {
  WarehouseTransferService,
  type WarehouseStockByLocation,
} from "@/services/warehouse-transfer.service";

interface TransferLineItem {
  sku: string;
  productName: string;
  availableQty: number;
  qty: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function TransferRequestModal({ open, onOpenChange, onCreated }: Props) {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [fromWarehouseId, setFromWarehouseId] = useState<string>("");
  const [toWarehouseId, setToWarehouseId] = useState<string>("");
  const [stockOptions, setStockOptions] = useState<WarehouseStockByLocation[]>([]);
  const [lines, setLines] = useState<TransferLineItem[]>([]);
  const [selectedSku, setSelectedSku] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [loadingStock, setLoadingStock] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load warehouses on open
  useEffect(() => {
    if (!open) return;
    WarehouseLocationsService.list().then(setWarehouses);
  }, [open]);

  // Load stock when source warehouse changes
  useEffect(() => {
    if (!fromWarehouseId) { setStockOptions([]); setLines([]); return; }
    setLoadingStock(true);
    WarehouseTransferService.listStockByWarehouse(fromWarehouseId)
      .then(setStockOptions)
      .finally(() => setLoadingStock(false));
    setLines([]);
    setSelectedSku("");
  }, [fromWarehouseId]);

  // Reset form when closed
  useEffect(() => {
    if (!open) {
      setFromWarehouseId("");
      setToWarehouseId("");
      setLines([]);
      setSelectedSku("");
      setNotes("");
      setStockOptions([]);
    }
  }, [open]);

  function handleAddSku() {
    if (!selectedSku) return;
    const stock = stockOptions.find((s) => s.sku === selectedSku);
    if (!stock) return;
    if (lines.some((l) => l.sku === selectedSku)) {
      toast.error("SKU already added");
      return;
    }
    setLines((prev) => [
      ...prev,
      { sku: stock.sku, productName: stock.productName, availableQty: stock.availableQuantity, qty: 1 },
    ]);
    setSelectedSku("");
  }

  function handleQtyChange(sku: string, raw: string) {
    const val = parseFloat(raw);
    if (isNaN(val) || val <= 0) return;
    setLines((prev) =>
      prev.map((l) => l.sku === sku ? { ...l, qty: Math.min(val, l.availableQty) } : l)
    );
  }

  function handleRemoveLine(sku: string) {
    setLines((prev) => prev.filter((l) => l.sku !== sku));
  }

  async function handleSubmit() {
    if (!fromWarehouseId || !toWarehouseId) {
      toast.error("Select both warehouses");
      return;
    }
    if (fromWarehouseId === toWarehouseId) {
      toast.error("Source and destination must be different");
      return;
    }
    if (lines.length === 0) {
      toast.error("Add at least one item");
      return;
    }

    setSubmitting(true);
    const result = await WarehouseTransferService.create({
      fromWarehouseId,
      toWarehouseId,
      notes: notes.trim() || undefined,
      items: lines.map((l) => ({ sku: l.sku, productName: l.productName, quantityRequested: l.qty })),
    });
    setSubmitting(false);

    if (!result) {
      toast.error("Failed to submit transfer request");
      return;
    }

    toast.success(`Transfer request ${result.refNumber} submitted`);
    onCreated();
    onOpenChange(false);
  }

  const availableSkus = stockOptions.filter((s) => !lines.some((l) => l.sku === s.sku));
  const canSubmit = !!fromWarehouseId && !!toWarehouseId && fromWarehouseId !== toWarehouseId && lines.length > 0 && !submitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Transfer Materials</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Empty state — no warehouses added yet */}
          {warehouses.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground text-center">
              <Loader2 className="h-5 w-5 opacity-30" />
              <p className="text-xs">No warehouses available.</p>
              <p className="text-[10px]">Go to <strong>Stock → Warehouses</strong> to add a warehouse first.</p>
            </div>
          )}

          {/* Warehouse selectors */}
          {warehouses.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium">
                Source <span className="text-destructive">*</span>
              </Label>
              <Select value={fromWarehouseId} onValueChange={setFromWarehouseId}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id} className="text-xs">{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium">
                Destination <span className="text-destructive">*</span>
              </Label>
              <Select value={toWarehouseId} onValueChange={setToWarehouseId}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.filter((w) => w.id !== fromWarehouseId).map((w) => (
                    <SelectItem key={w.id} value={w.id} className="text-xs">{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          )}

          {/* SKU picker + Notes — only shown when warehouses exist */}
          {warehouses.length > 0 && fromWarehouseId && (
            <div className="space-y-2">
              <Label className="text-xs font-medium">Items to Transfer</Label>
              {loadingStock ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading available stock…
                </div>
              ) : stockOptions.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No available stock at this warehouse.</p>
              ) : (
                <div className="flex gap-2">
                  <Select value={selectedSku} onValueChange={setSelectedSku}>
                    <SelectTrigger className="h-8 text-xs flex-1">
                      <SelectValue placeholder="Select SKU to add" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSkus.map((s) => (
                        <SelectItem key={s.sku} value={s.sku} className="text-xs">
                          {s.productName} ({s.sku}) — {s.availableQuantity} avail.
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleAddSku} disabled={!selectedSku}>
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
              )}

              {/* Line items */}
              {lines.length > 0 && (
                <div className="border rounded-md divide-y">
                  {lines.map((line) => (
                    <div key={line.sku} className="flex items-center gap-3 px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{line.productName}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{line.sku}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {line.availableQty} avail.
                      </Badge>
                      <Input
                        type="number"
                        min={1}
                        max={line.availableQty}
                        step={1}
                        value={line.qty}
                        onChange={(e) => handleQtyChange(line.sku, e.target.value)}
                        className="h-7 w-20 text-xs text-right"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-destructive"
                        onClick={() => handleRemoveLine(line.sku)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {warehouses.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs font-medium">Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for transfer, job context, etc."
                className="text-xs h-20 resize-none"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting
              ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Transferring…</>
              : "Transfer"
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
